/**
 * Session state for the SPA (WP0-AUTH). Published to the shell (WP0-SHELL):
 *
 *   <SessionProvider>            resolves the browser's session (active,
 *                                MFA outstanding, locked, signed out)
 *   <AuthGate>{app}</AuthGate>   sign-in, MFA and lock screens; idle lock
 *   useSession()                 the active SessionInfo or null
 *   useOfficeScope()             office selector state: offices, scope,
 *                                setScope, working office, allowAll
 *   usePermission(key, office?)  permission check that fails closed
 *
 * The office selection is stored on the server (it follows the person to
 * any workstation) and sent on every request as `x-velora-office`.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { LocationScope } from '@velora/contracts';
import type { OfficeSummary, SessionInfo, SessionPeek } from '@velora/contracts/identity';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AndroidSessionSync } from '../../app/android/AndroidHost.js';
import { androidSignOut } from '../../app/android/push.js';
import { ApiError, identityApi, identityKeys, setRequestScope } from './api.js';

export type AuthState =
  | { kind: 'loading' }
  | { kind: 'signed_out' }
  | { kind: 'mfa'; peek: SessionPeek }
  | { kind: 'enroll'; peek: SessionPeek }
  | { kind: 'locked'; peek: SessionPeek }
  | { kind: 'active'; session: SessionInfo }
  | { kind: 'unavailable'; message: string };

function fromPeek(peek: SessionPeek): AuthState | null {
  switch (peek.state) {
    case 'mfa_pending':
      return { kind: 'mfa', peek };
    case 'mfa_enroll':
      return { kind: 'enroll', peek };
    case 'locked':
      return { kind: 'locked', peek };
    case 'none':
      return { kind: 'signed_out' };
    default:
      return null;
  }
}

/**
 * Peeks first (always 200), so a signed-out or locked browser never asks
 * for the full session and never logs a 401; the session is read only when
 * the peek says it is active (or when the peek route is unavailable).
 */
export async function resolveAuthState(signal?: AbortSignal): Promise<AuthState> {
  let peek: SessionPeek | null = null;
  try {
    peek = await identityApi.peek(signal);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status >= 500) throw err;
  }
  const early = peek ? fromPeek(peek) : null;
  if (early) return early;
  try {
    return { kind: 'active', session: await identityApi.session(signal) };
  } catch (err) {
    if (!(err instanceof ApiError) || (err.status !== 401 && err.status !== 403)) throw err;
  }
  // The session ended between the two calls: report what is left of it.
  return fromPeek(await identityApi.peek(signal)) ?? { kind: 'signed_out' };
}

/* ------------------------------------------------------------------------ */
/* Other windows of this browser (pop-outs, other tabs)                      */
/* ------------------------------------------------------------------------ */

/**
 * Every Velora window of this browser shares one cookie session, so a
 * sign-out, lock or unlock in one is true in all of them. They hear it here
 * at once (instead of at the next 60 s check) and re-read the session; and
 * keyboard or mouse use in any window counts as activity for the idle lock
 * of all of them, so a pop-out left on a second monitor does not lock the
 * session while the person works in the main window. Memory only, same
 * origin only; nothing about the session is in the message.
 */
export const SESSION_CHANNEL = 'velora-session';

type SessionMessage = { t: 'state'; kind: AuthState['kind'] } | { t: 'activity' };

let sessionChannel: BroadcastChannel | null | undefined;
const sessionListeners = new Set<(m: SessionMessage) => void>();

function sessionBus(): BroadcastChannel | null {
  if (sessionChannel !== undefined) return sessionChannel;
  sessionChannel = null;
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return null;
  try {
    const ch = new BroadcastChannel(SESSION_CHANNEL);
    // Node's BroadcastChannel (tests) must not keep the process alive.
    (ch as BroadcastChannel & { unref?: () => void }).unref?.();
    ch.onmessage = (ev: MessageEvent<unknown>) => {
      const m = ev.data as SessionMessage | null;
      if (!m || typeof m !== 'object' || typeof m.t !== 'string') return;
      for (const l of [...sessionListeners]) l(m);
    };
    sessionChannel = ch;
  } catch {
    sessionChannel = null;
  }
  return sessionChannel;
}

function postSession(m: SessionMessage): void {
  try {
    sessionBus()?.postMessage(m);
  } catch {
    // A closing window: the others still notice at their next session check.
  }
}

function onSessionMessage(l: (m: SessionMessage) => void): () => void {
  sessionBus();
  sessionListeners.add(l);
  return () => sessionListeners.delete(l);
}

/** Session states another window should react to (not loading or a network blip). */
const SHARED_KINDS: ReadonlySet<AuthState['kind']> = new Set([
  'signed_out',
  'mfa',
  'enroll',
  'locked',
  'active',
]);

export interface SessionContextValue {
  state: AuthState;
  session: SessionInfo | null;
  /** Re-reads the session (after sign-in, unlock, office changes). */
  refresh: () => Promise<void>;
  scope: LocationScope | null;
  setScope: (scope: LocationScope) => Promise<void>;
  setWorkingOffice: (locationId: string | null) => Promise<void>;
  lockNow: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: identityKeys.session,
    queryFn: ({ signal }) => resolveAuthState(signal),
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    // Notices a lock or sign-out made elsewhere; background requests do not
    // reset the idle timer.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  const state = useMemo<AuthState>(
    () =>
      query.data
        ? query.data
        : query.isError
          ? {
              kind: 'unavailable',
              message: 'Velora cannot reach the server. Check the connection and try again.',
            }
          : { kind: 'loading' },
    [query.data, query.isError],
  );
  const session = state.kind === 'active' ? state.session : null;

  // Tell this browser's other windows when the session changes state here
  // (signed out, locked, unlocked, signed in), and re-read it when they do.
  const lastKind = useRef<AuthState['kind'] | null>(null);
  useEffect(() => {
    if (!SHARED_KINDS.has(state.kind)) return;
    const prev = lastKind.current;
    lastKind.current = state.kind;
    if (prev !== null && prev !== state.kind) postSession({ t: 'state', kind: state.kind });
  }, [state.kind]);
  useEffect(
    () =>
      onSessionMessage((m) => {
        if (m.t === 'state' && m.kind !== lastKind.current) {
          void qc.invalidateQueries({ queryKey: identityKeys.session });
        }
      }),
    [qc],
  );

  const [scope, setScopeState] = useState<LocationScope | null>(null);
  const serverScope = session?.context.scope ?? null;
  const serverScopeKey = serverScope ? JSON.stringify(serverScope) : '';
  useEffect(() => {
    setScopeState(serverScopeKey ? (JSON.parse(serverScopeKey) as LocationScope) : null);
  }, [serverScopeKey, session?.user.id]);
  useEffect(() => {
    setRequestScope(scope);
  }, [scope]);

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: identityKeys.session });
  }, [qc]);

  const setScope = useCallback(
    async (next: LocationScope) => {
      setScopeState(next);
      setRequestScope(next);
      await identityApi.updateContext({ scope: next });
      // Every office-scoped query depends on the selection.
      await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'identity' });
      await refresh();
    },
    [qc, refresh],
  );

  const setWorkingOffice = useCallback(
    async (locationId: string | null) => {
      await identityApi.updateContext({ workingLocationId: locationId });
      await refresh();
    },
    [refresh],
  );

  const lockNow = useCallback(async () => {
    try {
      await identityApi.lock();
    } finally {
      await refresh();
    }
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      // Velora for Android: retire this phone's push token first.
      await androidSignOut();
      await identityApi.logout();
    } finally {
      setRequestScope(null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'identity' });
      await refresh();
    }
  }, [qc, refresh]);

  const value = useMemo<SessionContextValue>(
    () => ({ state, session, refresh, scope, setScope, setWorkingOffice, lockNow, signOut }),
    [state, session, refresh, scope, setScope, setWorkingOffice, lockNow, signOut],
  );
  return (
    <SessionContext.Provider value={value}>
      <AndroidSessionSync kind={state.kind} userId={session?.user.id ?? null} />
      {children}
    </SessionContext.Provider>
  );
}

/** Provides a session unless an ancestor already does (pages work with or without the shell). */
export function EnsureSession({ children }: { children: ReactNode }) {
  const existing = useContext(SessionContext);
  return existing ? <>{children}</> : <SessionProvider>{children}</SessionProvider>;
}

export function useSessionContext(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSessionContext needs <SessionProvider>');
  return ctx;
}

export function useSession(): SessionInfo | null {
  return useSessionContext().session;
}

/** The session when a provider is mounted, else null (components used outside the shell). */
export function useOptionalSession(): SessionInfo | null {
  return useContext(SessionContext)?.session ?? null;
}

/** Permission check that fails closed (BUILD-PLAN 2.6, SimPLex canEditEvent). */
export function hasPermission(
  session: SessionInfo | null,
  key: string,
  locationId?: string | null,
): boolean {
  if (!session) return false;
  if (locationId) {
    const perOffice = session.locationPermissions[locationId];
    if (perOffice) return perOffice.includes(key);
  }
  return session.permissions.includes(key);
}

/** True when the user holds `key` practice-wide or in the given office. */
export function usePermission(key: string, locationId?: string | null): boolean {
  return hasPermission(useSession(), key, locationId);
}

export interface OfficeScopeValue {
  offices: OfficeSummary[];
  scope: LocationScope | null;
  setScope: (scope: LocationScope) => Promise<void>;
  workingLocationId: string | null;
  setWorkingOffice: (locationId: string | null) => Promise<void>;
  allowAll: boolean;
  canAddOffice: boolean;
  /** Office ids the current view covers (one, or every accessible office). */
  locationIds: string[];
}

/** What the top-bar office selector needs (ADR-0012). */
export function useOfficeScope(): OfficeScopeValue {
  const { session, scope, setScope, setWorkingOffice } = useSessionContext();
  const offices = session?.context.offices ?? [];
  const locationIds =
    scope === null ? [] : scope.kind === 'all' ? offices.map((o) => o.id) : [scope.locationId];
  return {
    offices,
    scope,
    setScope,
    workingLocationId: session?.context.workingLocationId ?? null,
    setWorkingOffice,
    allowAll: session?.context.allowAll ?? false,
    canAddOffice: session?.context.canAddOffice ?? false,
    locationIds,
  };
}

/* ------------------------------------------------------------------------ */
/* Idle lock (client side; the server enforces it too)                       */
/* ------------------------------------------------------------------------ */

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove'] as const;

/**
 * Locks the screen after the workstation's idle limit with no keyboard,
 * mouse or touch input. The server applies the same limit to API requests,
 * so a forgotten tab cannot be used after the limit either.
 */
export function useIdleLock(
  idleSeconds: number | null,
  onIdle: () => void,
  now: () => number = Date.now,
) {
  const last = useRef(now());
  useEffect(() => {
    if (!idleSeconds) return undefined;
    last.current = now();
    let lastMove = 0;
    const mark = (e: Event) => {
      const t = now();
      if (e.type === 'mousemove') {
        if (t - lastMove < 1000) return;
        lastMove = t;
      }
      last.current = t;
    };
    // Activity in another window of this browser counts too (sent at most
    // every 5 s from each window).
    let lastSent = 0;
    const share = () => {
      const t = now();
      if (t - lastSent < 5_000) return;
      lastSent = t;
      postSession({ t: 'activity' });
    };
    const markAndShare = (e: Event) => {
      const before = last.current;
      mark(e);
      if (last.current !== before) share();
    };
    const offRemote = onSessionMessage((m) => {
      if (m.t === 'activity') last.current = now();
    });
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, markAndShare, { passive: true });
    const timer = window.setInterval(() => {
      if (now() - last.current >= idleSeconds * 1000) {
        last.current = now();
        onIdle();
      }
    }, 5_000);
    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, markAndShare);
      window.clearInterval(timer);
      offRemote();
    };
  }, [idleSeconds, onIdle, now]);
}
